import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { 
  BookOpen, 
  Trophy, 
  Clock, 
  ChevronRight,
  CheckCircle2,
  PlayCircle
} from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sops, setSops] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sopsRes, progressRes] = await Promise.all([
        apiClient.get("/sops"),
        apiClient.get("/progress"),
      ]);
      setSops(sopsRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressForSop = (sopId) => {
    return progress.find((p) => p.sop_id === sopId);
  };

  const completedCount = progress.filter((p) => p.completed).length;
  const inProgressCount = progress.filter((p) => !p.completed && p.completed_steps?.length > 0).length;
  const totalSops = sops.length;
  const overallProgress = totalSops > 0 ? Math.round((completedCount / totalSops) * 100) : 0;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  // Group SOPs by section
  const sopsBySection = sops.reduce((acc, sop) => {
    const section = sop.section || "operations";
    if (!acc[section]) acc[section] = [];
    acc[section].push(sop);
    return acc;
  }, {});

  const sectionLabels = {
    foundations: "Foundations",
    guest_journey: "Guest Journey",
    excellence: "Service Excellence",
    operations: "Operations",
    expectations: "Standards"
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="skeleton h-40 w-full"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="skeleton h-28"></div>
            <div className="skeleton h-28"></div>
            <div className="skeleton h-28"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in-up max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="relative overflow-hidden rounded-sm rd-green-gradient p-8 lg:p-10">
          <div className="absolute top-4 right-4 opacity-10">
            <img
              src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
              alt=""
              className="w-32 h-32 object-contain"
            />
          </div>
          <div className="relative z-10">
            <p className="text-[var(--rd-gold)] text-sm font-medium tracking-wider uppercase mb-2">
              {getGreeting()}
            </p>
            <h1 className="text-3xl lg:text-4xl text-white mb-3" style={{ fontFamily: "'Cinzel', serif" }}>
              Welcome, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-white/70 text-lg max-w-xl">
              Continue your training to deliver the 5-star Royal Dornoch experience.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <div className="flex-1 max-w-sm">
                <div className="flex justify-between text-sm text-white/80 mb-2">
                  <span>Training Progress</span>
                  <span>{overallProgress}%</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${overallProgress}%`,
                      background: `linear-gradient(90deg, var(--rd-gold), #e5c270)`
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="stat-card stagger-1 animate-fade-in-up" data-testid="stat-total-sops">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Total Modules
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {totalSops}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[var(--rd-green)]/10 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[var(--rd-green)]" />
              </div>
            </div>
          </div>

          <div className="stat-card stagger-2 animate-fade-in-up" data-testid="stat-completed">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Completed
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {completedCount}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[var(--rd-gold)]/10 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-[var(--rd-gold)]" />
              </div>
            </div>
          </div>

          <div className="stat-card stagger-3 animate-fade-in-up" data-testid="stat-in-progress">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  In Progress
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {inProgressCount}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-blue-50 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Training Modules by Section */}
        {Object.entries(sopsBySection).map(([section, sectionSops]) => (
          <div key={section}>
            <div className="section-header">
              <h2 className="text-xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                {sectionLabels[section] || section}
              </h2>
              <div className="section-header-line"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {sectionSops.map((sop, index) => {
                const sopProgress = getProgressForSop(sop.id);
                const stepsCompleted = sopProgress?.completed_steps?.length || 0;
                const totalSteps = sop.steps?.length || 0;
                const isCompleted = sopProgress?.completed;
                // If module is completed, show 100%, otherwise calculate from steps
                const progressPercent = isCompleted ? 100 : (totalSteps > 0 ? Math.round((stepsCompleted / totalSteps) * 100) : 0);

                return (
                  <div
                    key={sop.id}
                    className={`sop-card stagger-${(index % 4) + 1} animate-fade-in-up`}
                    onClick={() => navigate(`/sops/${sop.id}`)}
                    data-testid={`sop-card-${sop.id}`}
                  >
                    <div className="sop-card-image">
                      <img
                        src={sop.image_url || "https://images.pexels.com/photos/1325706/pexels-photo-1325706.jpeg"}
                        alt={sop.title}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      {isCompleted && (
                        <div className="absolute top-3 right-3">
                          <span className="badge-gold flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Complete
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-[10px] font-semibold tracking-wider uppercase text-[var(--rd-gold)] mb-1">
                        {sop.category.replace("_", " ")}
                      </p>
                      <h3 className="text-base font-semibold text-[var(--rd-green)] mb-3 line-clamp-2" style={{ fontFamily: "'Cinzel', serif" }}>
                        {sop.title}
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-[hsl(var(--muted-foreground))]">
                            {stepsCompleted}/{totalSteps} steps
                          </span>
                          <span className="font-medium text-[var(--rd-green)]">{progressPercent}%</span>
                        </div>
                        <div className="rd-progress-track">
                          <div className="rd-progress-fill" style={{ width: `${progressPercent}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
